/**
 * Zod schemas for the Refine admin events CRUD surface (API-1468..71).
 *
 * Distinct from the public/event ingest schemas in `events.ts`:
 *   - admin creates/updates set `source = 'admin'` (server-side; not in body)
 *   - admin can target any `status` value including `DRAFT` and `CANCELLED`
 *   - admin updates carry an `updatedAt` token for optimistic concurrency
 *   - input shape mirrors the Refine resource-form convention (PATCH = same
 *     body as POST, all fields optional except the concurrency token)
 *
 * Error envelope: the route handler reshapes Zod failures into the canonical
 * `{ error, code, message }` envelope. Do NOT route these through
 * `createErrorResponse()` from `errors.ts` — that emits the
 * `{ details, timestamp, requestId }` envelope which the Refine client does
 * not expect.
 */
import { z } from 'zod';
import {
  EVENT_LIFECYCLE_STATUSES,
  QUALITY_REASON_CODES,
  type QualityReasonCode,
} from '@loci/types';

/**
 * Event status values the admin form can write. Derived from the single source
 * of truth `EVENT_LIFECYCLE_STATUSES` in `@loci/types` (API-1619), which is
 * itself pinned to the Prisma `EventStatus` enum at compile time
 * (`services/backend-api/src/utils/browseEventStatus.ts`). The validation
 * package cannot import db-core's generated client without pulling Prisma into
 * every consumer, so it shares the hand-maintained-but-Prisma-pinned tuple —
 * covering every member including `OFFSALE`, and unable to drift going forward.
 */
export const adminEventStatusSchema = z.enum(EVENT_LIFECYCLE_STATUSES);
export type AdminEventStatus = z.infer<typeof adminEventStatusSchema>;

/**
 * Data-quality placement status values an editor may manually assign via the
 * admin update (API-1508). Mirrors the Prisma `QualityStatus` enum in
 * `packages/db-core/prisma/schema.prisma` (and the `QualityStatus` string
 * union in `@loci/types`). Kept in sync manually — the validation package
 * cannot import db-core's generated client.
 */
export const adminQualityStatusSchema = z.enum([
  'PREMIUM',
  'STANDARD',
  'LOW_CONFIDENCE',
  'QUARANTINED',
]);
export type AdminQualityStatus = z.infer<typeof adminQualityStatusSchema>;

/**
 * Machine-readable quality reason code (API-1517). Built from the canonical
 * runtime list in `@loci/types` (`QUALITY_REASON_CODES`, itself derived from
 * the `QUALITY_REASON_SEVERITY` map keys) so the accepted filter values can
 * never drift from the codes the authoritative scorer can emit.
 */
export const adminQualityReasonCodeSchema = z.enum([...QUALITY_REASON_CODES] as [
  QualityReasonCode,
  ...QualityReasonCode[],
]);
export type AdminQualityReasonCode = z.infer<typeof adminQualityReasonCodeSchema>;

/**
 * Trimmed, non-empty string of bounded length. Bounds are chosen to match
 * the underlying Postgres columns — `title` is `varchar(500)` in practice,
 * `description` is `text` but we cap at 50k to keep audit-log diffs bounded.
 */
const titleSchema = z.string().trim().min(1).max(500);
const descriptionSchema = z.string().trim().min(1).max(50_000);
const imageUrlSchema = z.string().trim().url().max(2048);

/**
 * Price is accepted as a STRING because Postgres `Decimal` doesn't survive
 * JSON round-trips as a `number` without lossy float coercion. The string
 * must parse as a non-negative decimal with up to two fractional digits
 * (USD cents-level precision). Frontends send `"15.00"`, not `15`.
 */
const priceStringSchema = z
  .string()
  .trim()
  .regex(
    /^\d+(\.\d{1,2})?$/,
    'Price must be a non-negative decimal with up to 2 fractional digits',
  );

/**
 * ISO 8601 datetime in UTC. Refine ships ISO strings from the Mantine /
 * shadcn date pickers; we don't accept Unix epochs.
 */
const isoDateTimeSchema = z
  .string()
  .trim()
  .min(1)
  .refine((value) => {
    const ms = Date.parse(value);
    return Number.isFinite(ms);
  }, 'Must be an ISO 8601 datetime string');

/**
 * CUID/UUID-ish identifier. Matches `adminIdParamSchema` in `../admin.ts`
 * shape — non-empty trimmed string, bounded length. Not enforcing CUID
 * format here because Prisma occasionally migrates id strategies and we
 * want the validator to keep working across them.
 */
const idSchema = z.string().trim().min(1).max(128);

/**
 * Path param guard for `GET/PATCH/DELETE /api/v1/admin/events/:id` — same
 * shape as `adminVenueIdParamSchema`. Rejects empty/whitespace-only and
 * over-long ids with `400 INVALID_PARAM` before any data access.
 */
export const adminEventIdParamSchema = idSchema;

/**
 * Body for `POST /api/v1/admin/events`.
 *
 * Server-controlled fields (`source = 'admin'`, `createdBy = req.user.id`)
 * are intentionally absent — they're set by the route handler.
 */
export const adminCreateEventSchema = z
  .object({
    title: titleSchema,
    description: descriptionSchema.optional(),
    /** Show-start time in ISO 8601 (UTC). */
    startTime: isoDateTimeSchema,
    /** Optional show-end time in ISO 8601 (UTC). Must be ≥ startTime if present. */
    endTime: isoDateTimeSchema.optional(),
    locationId: idSchema,
    categoryId: idSchema,
    imageUrl: imageUrlSchema.optional(),
    status: adminEventStatusSchema,
    /** Decimal-as-string. Empty/absent means "price unknown". */
    price: priceStringSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.endTime !== undefined) {
      const start = Date.parse(value.startTime);
      const end = Date.parse(value.endTime);
      if (end < start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endTime'],
          message: 'endTime must be at or after startTime',
        });
      }
    }
  });
export type AdminCreateEventBody = z.infer<typeof adminCreateEventSchema>;

/**
 * Body for `PATCH /api/v1/admin/events/:id`.
 *
 * Every field from the create body becomes optional EXCEPT `updatedAt`,
 * which is the optimistic-concurrency token. The handler will:
 *   1. Look up the current row.
 *   2. Compare `current.updatedAt.toISOString()` to `body.updatedAt`.
 *   3. Return 409 CONFLICT if they don't match.
 *
 * The `endTime ≥ startTime` cross-field check only fires when BOTH are
 * present in the patch body (otherwise we'd need to merge with the
 * persisted row, which is the handler's job).
 */
export const adminUpdateEventSchema = z
  .object({
    title: titleSchema.optional(),
    description: descriptionSchema.nullable().optional(),
    startTime: isoDateTimeSchema.optional(),
    endTime: isoDateTimeSchema.nullable().optional(),
    locationId: idSchema.optional(),
    categoryId: idSchema.optional(),
    imageUrl: imageUrlSchema.nullable().optional(),
    status: adminEventStatusSchema.optional(),
    price: priceStringSchema.nullable().optional(),
    /**
     * Manual data-quality override (API-1508).
     *   - one of the four enum values → override the machine verdict and set
     *     `Event.qualityStatusOverriddenAt = now()`; the override wins over the
     *     scorer until cleared.
     *   - `null` → CLEAR the override (re-enable auto-scoring). The next
     *     ingest/refresh re-scores the row normally.
     *   - omitted → no change to quality fields.
     */
    qualityStatus: adminQualityStatusSchema.nullable().optional(),
    /**
     * Optional free-text note recorded on the `ReviewAction` audit row when a
     * `qualityStatus` override (or clear) is applied. Capped to keep the audit
     * surface bounded. Ignored when `qualityStatus` is absent.
     */
    qualityStatusNote: z.string().trim().max(1000).optional(),
    /**
     * Optimistic-concurrency token — required. Must equal
     * `currentRow.updatedAt.toISOString()` for the update to apply.
     */
    updatedAt: isoDateTimeSchema,
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.startTime !== undefined && value.endTime !== undefined && value.endTime !== null) {
      const start = Date.parse(value.startTime);
      const end = Date.parse(value.endTime);
      if (end < start) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['endTime'],
          message: 'endTime must be at or after startTime',
        });
      }
    }
  });
export type AdminUpdateEventBody = z.infer<typeof adminUpdateEventSchema>;

/**
 * Body for `DELETE /api/v1/admin/events/:id`.
 *
 * `reason` is optional free-text capped at 500 chars (audit-log surface).
 * The handler still attempts the soft-delete regardless of body shape —
 * an empty body is acceptable.
 */
export const adminDeleteEventSchema = z
  .object({
    reason: z.string().trim().max(500).optional(),
  })
  .strict();
export type AdminDeleteEventBody = z.infer<typeof adminDeleteEventSchema>;

/**
 * Strict CUID path-param guard for `POST /api/v1/admin/events/:id/decide`
 * (API-1581).
 *
 * The moderation decision endpoint changes what the PUBLIC sees, so it
 * tightens `:id` validation beyond the lenient `adminEventIdParamSchema`
 * (an opaque trimmed string): it enforces the actual `Event.id` shape
 * (`@default(cuid())` in `schema.prisma`) via Zod's built-in `.cuid()` — the
 * same validator the public schemas already use for cuid ids (see
 * `schemas.ts` `locationId: z.string().cuid()`). A non-CUID `:id` is rejected
 * with `400` before any data access, shrinking the attack surface of a
 * high-risk mutation.
 */
export const adminDecideEventIdParamSchema = z.string().trim().cuid();

/**
 * Moderation decision verbs for `POST /api/v1/admin/events/:id/decide`
 * (API-1581 + WEB-608). The handler maps each to a `qualityStatus` / hold
 * transition OR a category-taxonomy confirmation (WEB-608 / KTD6 / KTD10):
 *   - `approve`  → release (`qualityStatus = STANDARD`) + re-score, so the
 *     event auto-publishes to public surfaces (wraps the API-1508 PATCH
 *     quality-status flow, but un-pinned so the authoritative scorer
 *     re-derives the verdict from content).
 *   - `reject`   → hold (`qualityStatus = QUARANTINED`, pinned override) so the
 *     event stays out of public surfaces.
 *   - `escalate` → keep held, mark the `FlaggedEvent.reviewStatus = escalated`
 *     for a second reviewer.
 *   - `confirm-category` → keep the best-guess primary, stamp it
 *     `operator-confirmed`, clear the open `low-confidence-category` flag,
 *     write a `ReviewAction`. Does **not** touch `Event.qualityStatus` (KTD8).
 *   - `correct-category` → replace the primary with `categoryId`, stamp
 *     `operator-confirmed`, clear the open `low-confidence-category` flag,
 *     write a `ReviewAction`. Does **not** touch `Event.qualityStatus` (KTD8).
 */
export const adminEventDecisionSchema = z.enum([
  'approve',
  'reject',
  'escalate',
  'confirm-category',
  'correct-category',
]);
export type AdminEventDecision = z.infer<typeof adminEventDecisionSchema>;

/**
 * Body for `POST /api/v1/admin/events/:id/decide` (API-1581 + WEB-608).
 *
 * `.strict()` so an unknown key — or an unknown `decision` value — is a
 * `400 INVALID_PAYLOAD`. `note` is optional free-text capped at 1000 chars,
 * recorded on the `ReviewAction` audit row and the `FlaggedEvent.flagReason`.
 * `categoryId` is required for `correct-category` and optional for
 * `confirm-category` (defaults to the event's current primary).
 */
export const adminDecideEventSchema = z
  .object({
    decision: adminEventDecisionSchema,
    note: z.string().trim().max(1000).optional(),
    categoryId: idSchema.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.decision === 'correct-category' && !value.categoryId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['categoryId'],
        message: 'categoryId is required when correcting a category',
      });
    }
  });
export type AdminDecideEventBody = z.infer<typeof adminDecideEventSchema>;

/**
 * Query schema for `GET /api/v1/admin/events`.
 *
 * Strict bounds protect downstream Prisma queries from `take` blowups
 * and unbounded sort/filter strings.
 */
export const ADMIN_EVENTS_LIST_DEFAULT_PAGE_SIZE = 20;
export const ADMIN_EVENTS_LIST_MAX_PAGE_SIZE = 200;

export const adminEventsListSortFieldSchema = z.enum(['title', 'startTime', 'category', 'status']);
export const adminEventsListSortDirectionSchema = z.enum(['asc', 'desc']);

/**
 * `FlaggedEvent.flagCategory` values the admin events list can filter on
 * (API-1638). Today only the category-taxonomy review queue
 * (`low-confidence-category`) is exposed; expand this enum as more
 * flag-driven admin queues land on the events list surface.
 *
 * Spelled as the same documented string the data-pipeline writer uses
 * (`LOW_CONFIDENCE_CATEGORY_FLAG` in categorizeEvent / categoryCounts) —
 * the shared contract is the `flagCategory` column's documented value set.
 */
export const ADMIN_EVENTS_FLAG_FILTERS = ['low-confidence-category'] as const;
export const adminEventsFlagFilterSchema = z.enum(ADMIN_EVENTS_FLAG_FILTERS);
export type AdminEventsFlagFilter = z.infer<typeof adminEventsFlagFilterSchema>;

export const adminEventsListQuerySchema = z
  .object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
      .number()
      .int()
      .min(1)
      .max(ADMIN_EVENTS_LIST_MAX_PAGE_SIZE)
      .default(ADMIN_EVENTS_LIST_DEFAULT_PAGE_SIZE),
    /**
     * Format: `<field>:<direction>` (e.g. `startTime:desc`). When omitted the
     * route defaults to event date ASCENDING (soonest-upcoming first) — see
     * `buildOrderBy` in the admin events route. The default browse also hides
     * past events (admin policy 2026-06-29); the `Past` status tab
     * (`status=COMPLETED`) plus an explicit text search or date window are the
     * opt-ins to surface them.
     */
    sort: z
      .string()
      .trim()
      .regex(/^[a-zA-Z]+:(asc|desc)$/, "Sort must be '<field>:<asc|desc>'")
      .optional(),
    /** Free-text title filter (ILIKE `%q%`). Trimmed; empty rejected. */
    q: z.string().trim().min(1).max(200).optional(),
    /** Category slug or id. The handler resolves both shapes. */
    category: z.string().trim().min(1).max(120).optional(),
    status: adminEventStatusSchema.optional(),
    dateFrom: isoDateTimeSchema.optional(),
    dateTo: isoDateTimeSchema.optional(),
    /**
     * Filter events by venue (Location) id. Added in WEB-362 to support the
     * drill-through link `/admin/venues` → `/admin/events?venueId=<uuid>`.
     * Matches `events.locationId = $venueId` — single venue per event in the
     * current schema. Sent as `?venueId=<uuid>` by the dataProvider's `eq`
     * filter mapping; the route forwards it to the SQL builder unchanged.
     */
    venueId: idSchema.optional(),
    /**
     * Filter by data-quality placement band (API-1517), e.g.
     * `?qualityStatus=QUARANTINED` to list quarantined rows for manual
     * remediation. Values mirror the Prisma `QualityStatus` enum via
     * `adminQualityStatusSchema`. The admin list is deliberately
     * quality-gate exempt (raw SQL — API-1513), so quarantined rows are
     * returned even when `LOCI_QUALITY_GATE_ENABLED=true`.
     */
    qualityStatus: adminQualityStatusSchema.optional(),
    /**
     * Filter by a machine-readable quality reason code (API-1517), e.g.
     * `?reason=LOCATION_NAME_IS_ADDRESS` to find events quarantined because
     * their venue "name" is a raw street address (COR-25). Matches rows
     * whose `qualityReasons` array CONTAINS the code. Typically combined
     * with `qualityStatus=QUARANTINED` for the remediation queue.
     */
    reason: adminQualityReasonCodeSchema.optional(),
    /**
     * Filter by an open `FlaggedEvent.flagCategory` (API-1638), e.g.
     * `?flag=low-confidence-category` for the category-taxonomy review queue.
     * Keyed off the flag (+ primary `EventCategory.confidence` on the row),
     * NOT `Event.qualityStatus` (KTD8). Only open (non-terminal) flags match.
     */
    flag: adminEventsFlagFilterSchema.optional(),
    /**
     * Whether to include soft-deleted rows (`deletedAt IS NOT NULL`).
     * Default is false so the list view matches what the public surface
     * sees. Operators recovering accidentally-deleted events flip this on.
     */
    includeDeleted: z
      .union([z.literal('true'), z.literal('false'), z.boolean()])
      .transform((v) => v === true || v === 'true')
      .default(false),
  })
  .strict();
export type AdminEventsListQuery = z.infer<typeof adminEventsListQuerySchema>;

/** Resolved sort tuple after parsing the `sort` query param. */
export interface AdminEventsListSort {
  field: z.infer<typeof adminEventsListSortFieldSchema>;
  direction: z.infer<typeof adminEventsListSortDirectionSchema>;
}

/**
 * Parse a `sort=<field>:<direction>` string into a typed tuple. Returns
 * `null` when the sort key is not on the allow-list (the route handler
 * treats `null` as "fall back to the default sort").
 */
export function parseAdminEventsListSort(raw: string | undefined): AdminEventsListSort | null {
  if (!raw) return null;
  const [rawField, rawDirection] = raw.split(':');
  const fieldResult = adminEventsListSortFieldSchema.safeParse(rawField);
  const directionResult = adminEventsListSortDirectionSchema.safeParse(rawDirection);
  if (!fieldResult.success || !directionResult.success) {
    return null;
  }
  return { field: fieldResult.data, direction: directionResult.data };
}
